import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import { API_ROUTES } from '../consts/api-routes.const';
import { SKIP_SERVER_ERROR_RETRY } from '../interceptors/api-error.interceptor';
import { SessionService } from '../pages/appointment/authenticate/services/session.service';
import { HttpService } from './http.service';

/** Mileage snapshot from OCW 2.0 (GET /wp/planner/vehicle-connectivity/{plate}/mileage). */
export interface VehicleConnectivityMileage {
  mileage: number;
  updatedAt: string;
  /** SW-483 — backend-oordeel: de meting is oud genoeg om de planner te laten bevestigen. */
  showNotification: boolean;
}

export interface VehicleServiceInterval {
  dueInDays: number | null;
  dueInKilometers: number | null;
}

/** Next service inspection and oil service from OCW 2.0 (GET /wp/planner/vehicle-connectivity/{plate}/service-intervals). */
export interface VehicleConnectivityServiceIntervals {
  serviceInspection: VehicleServiceInterval | null;
  oilService: VehicleServiceInterval | null;
}

@Injectable({ providedIn: 'root' })
export class OcwService {
  private readonly httpService: HttpService = inject(HttpService);
  private readonly sessionService: SessionService = inject(SessionService);

  // Best-effort lookup: any failure (unknown vehicle, no connectivity, 5xx) or a
  // non-positive mileage resolves to null so the caller falls back to manual input.
  // Legal: an anonymous visitor (guest flow — e-mail known, no 2FA session) may not
  // trigger the OCW lookup at all, so gate here rather than at each call site.
  public getMileage(licensePlate: string): Observable<VehicleConnectivityMileage | null> {
    if (!licensePlate || !this.sessionService.isAuthenticated()) {
      return of(null);
    }

    const context: HttpContext = new HttpContext().set(SKIP_SERVER_ERROR_RETRY, true);

    return this.httpService
      .get<VehicleConnectivityMileage>(
        API_ROUTES.VEHICLE.connectivityMileage(licensePlate),
        context,
      )
      .pipe(
        map((response: VehicleConnectivityMileage | null): VehicleConnectivityMileage | null =>
          response && typeof response.mileage === 'number' && response.mileage > 0
            ? // Ontbreekt showNotification (oudere API), dan niet vragen: de bevestiging
              // hoort alleen te verschijnen wanneer de backend er expliciet om vraagt.
              { ...response, showNotification: response.showNotification === true }
            : null,
        ),
        catchError((): Observable<null> => of(null)),
      );
  }

  public getServiceIntervals(
    licensePlate: string,
  ): Observable<VehicleConnectivityServiceIntervals | null> {
    if (!licensePlate || !this.sessionService.isAuthenticated()) {
      return of(null);
    }

    const context: HttpContext = new HttpContext().set(SKIP_SERVER_ERROR_RETRY, true);

    return this.httpService
      .get<VehicleConnectivityServiceIntervals>(
        API_ROUTES.VEHICLE.connectivityServiceIntervals(licensePlate),
        context,
      )
      .pipe(
        map(
          (
            response: VehicleConnectivityServiceIntervals | null,
          ): VehicleConnectivityServiceIntervals | null =>
            response
              ? {
                  serviceInspection: response.serviceInspection ?? null,
                  oilService: response.oilService ?? null,
                }
              : null,
        ),
        catchError((): Observable<null> => of(null)),
      );
  }
}
